/*
 * File: signIn.styles.ts
 * Project: meki
 * File Created: Wednesday, 7th December 2022 11:16:24 am
 * Author: Blanca Munizaga (blanca@inventures.cl)
 * -----
 * Last Modified: Thursday, 6th July 2023 5:34:35 pm
 * Modified By: Gabriel Ulloa (gabriel@inventures.cl)
 * -----
 * Copyright 2019 - 2022 Incrementa Ventures SpA. ALL RIGHTS RESERVED
 * Terms and conditions defined in license.txt
 * -----
 * Inventures - www.inventures.cl
 */
import { styled } from '@mui/system';
import { Button, ListItemText, Select, Typography } from '@mui/material';
import ReactCodeInput from 'react-code-input';
import { ContentContainer } from '@components/ContentContainer';
interface DisableProps {
  disable: boolean;
}

const ContentContainerDiv = styled(ContentContainer)({
  backgroundColor: '#F9F9F9',
});
const ContentDiv = styled('div')({
  display: 'flex',
  padding: '16px 12px',
  flexDirection: 'column',
  minHeight: 'calc(100vh - 64px - 80px)',
  '@media (min-width: 900px)': {
    minHeight: 'calc(88vh - 64px - 80px)',
  },
});
const CenterDiv = styled('div')({
  border: '1px solid #EAEAEA',
  backgroundColor: 'white',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '17px 15px',
  borderRadius: '10px',
  alignItems: 'center',
});
const Form = styled('form')({
  maxWidth: '350px',
  marginTop: '17px',
});
const AskDataTypography = styled(Typography)({
  marginBottom: '8px',
  fontFamily: 'Poppins',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
  color: '#414046',
  textAlign: 'left',
});
const WhyDataTypography = styled(Typography)({
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '18px',
  letterSpacing: '0.15px',
  color: '#757475',
  textAlign: 'left',
  margin: '0 0px 12px 0px',
});
const GoogleButton = styled(Button)({
  backgroundColor: '#eaeaea',
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '18px',
  color: '#414046',
  margin: '21px 15px 150px 15px',
  textTransform: 'none',
  width: '100%',
  maxWidth: '350px',
  '@media (min-width: 576px)': {
    margin: '21px 15px 200px 15px',
  },
  '&:hover': {
    backgroundColor: '#d5d5d5',
  },
});
const DividerDiv = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
});
const DividerLine = styled('div')({
  width: '20%',
  minWidth: '142px',
  height: '1px',
  backgroundColor: '#C5C5C5',
});
const DividerText = styled(Typography)({
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  color: '#C5C5C5',
  letterSpacing: '0.15px',
  textTransform: 'none',
  margin: '35px 18px 30px 18px',
  maxWidth: '350px',
});
const InputDiv = styled('div')({
  display: 'flex',
  flexDirection: 'row',
});
const PhonePrefixSelect = styled(Select)({
  width: '150px',
  marginRight: '10px',
  maxHeight: '40px',
});
const IconListItemText = styled(ListItemText)({
  fontSize: '32px',
  marginRight: '10px',
  maxWidth: '16px',
});
const InsertCodeTypography = styled(Typography)({
  marginTop: '36px',
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
  color: '#414046',
  textAlign: 'center',
  marginBottom: '8px',
});
const InsertCodeInfoTypography = styled(Typography)({
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 400,
  width: '70%',
  fontSize: '12px',
  lineHeight: '18px',
  letterSpacing: '0.15px',
  color: '#414046',
  textAlign: 'center',
  marginBottom: '8px',
  '> span': {
    color: '#0A7ABE',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
});
const InlinePhoneContainer = styled('div')({
  display: 'inline-block',
  marginRight: '5px',
});
const ResendCodeButton = styled(Button)<DisableProps>(({ disable }) => ({
  marginTop: '60px',
  marginBottom: '5px',
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '16px',
  textAlign: 'center',
  letterSpacing: '0.15px',
  textDecoration: 'none',
  color: disable ? '#AFAFAF' : '#0A7ABE',
  textTransform: 'none',
}));
const TryDifferentWayButton = styled(Button)({
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '16px',
  textAlign: 'center',
  letterSpacing: '0.15px',
  textDecoration: 'none',
  color: '#0A7ABE',
  textTransform: 'none',
  marginBottom: '221px',
});
const CenterCodeInput = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
});
const CodeInput = styled(ReactCodeInput)((prop) => ({
  input: {
    display: 'flex',
    flexDirection: 'row',
    textAlign: 'center',
    justifyContent: 'center',
    fontFamily: 'Poppins',
    margin: '4px 3px',
    width: '36px',
    borderRadius: '4px',
    fontSize: '20px',
    lineHeight: '23px',
    height: '48px',
    backgroundColor: '#FFFFFF',
    color: '#414046 !important',
    border: prop.isValid ? 'grey solid 1px' : '0.8px solid red !important',
    '&:focus': {
      border: '1px solid #0A7ABE !important',
      outline: '#c8c8c8 solid 1px',
    },
    '&:nth-child(1)': {
      order: '1 !important',
    },
    '&:nth-child(2)': {
      order: '2 !important',
    },
    '&:nth-child(3)': {
      order: '3 !important',
    },
    '&:nth-child(4)': {
      order: '5 !important',
    },
    '&:nth-child(5)': {
      order: '6 !important',
    },
    '&:nth-child(6)': {
      order: '7 !important',
    },
    '&:nth-child(7)': {
      order: '8 !important',
      display: 'none !important',
    },
  },
  display: 'flex !important',
  alignItems: 'center !important',
  justifyContent: 'center !important',
  '&::after': {
    content: '""',
    margin: '0px 0px !important',
    width: '15px !important',
    height: '3px !important',
    backgroundColor: 'withe !important',
    order: '4 !important',
  },
}));

const IncorrectCodeTypography = styled(Typography)({
  marginTop: '10px',
  marginBottom: '-34px',
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
  color: 'red',
  textAlign: 'center',
});
const ButtonDiv = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'center',
  alignItems: 'center',
  position: 'sticky',
  bottom: '0',
  width: '100%',
  height: '80px',
  backgroundColor: 'white',
  borderTop: '2px solid #EAEAEA',
});

const ButtonStyled = styled('button')({
  backgroundColor: '#0A7ABE',
  borderRadius: '100px',
  padding: '8px 60px',
  cursor: 'pointer',
  border: 'none',
  color: 'white',
});

const TypographyButton = styled(Typography)({
  fontFamily: 'Poppins',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
  color: 'white',
});

const GooglePlaceHolder = styled('div')({
  height: '300px',
  '@media (min-width: 576px)': {
    height: '350px',
  },
});

const LoaderComponentContainer = styled('div')({
  position: 'absolute',
  top: '40vh',
  left: 'calc(50vw - 150px)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
  alignItems: 'center',
  height: '20vh',
  paddingTop: '5vh',
  paddingBottom: '5vh',
  width: 300,
  borderRadius: '10px',
  backgroundColor: '#FFFFFF',
  zIndex: 4002,
});

const GreyOverlay = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 4001,
});

const CheckContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '0px',
  alignItems: 'center',
  justifyContent: 'center',
  justifyItems: 'center',
});

export {
  ContentContainerDiv,
  ContentDiv,
  CenterDiv,
  Form,
  AskDataTypography,
  WhyDataTypography,
  GoogleButton,
  DividerDiv,
  DividerLine,
  DividerText,
  InputDiv,
  PhonePrefixSelect,
  IconListItemText,
  InsertCodeTypography,
  ResendCodeButton,
  TryDifferentWayButton,
  CenterCodeInput,
  CodeInput,
  IncorrectCodeTypography,
  ButtonDiv,
  ButtonStyled,
  TypographyButton,
  GooglePlaceHolder,
  LoaderComponentContainer,
  GreyOverlay,
  InsertCodeInfoTypography,
  CheckContainer,
  InlinePhoneContainer,
};
